<template>
  <div class="iq-ai-response" :class="{ 'iq-ai-response--light': surface === 'light' }">
    <div class="iq-ai-response-icon-wrapper">
      <div class="">
        <Icon name="iq_large_icon" />
      </div>
    </div>
    <div class="iq-ai-response-content">
      <!-- Thinking/Reasoning Dropdown (Gemini-style) -->
      <IQThinkingDropdown
        v-if="responseData.reasoning || isThinking"
        :reasoning="responseData.reasoning || currentReasoning"
        :isThinking="isThinking"
        :autoExpand="true"
        :variant="surface === 'light' ? 'light' : 'default'"
      />
      
      <!-- Structured Ask AI answer ({text, blocks[], sources[]}) -->
      <div
        v-if="isStructuredEnabled && isStructuredAnswer"
        class="iq-ai-answer-blocks"
      >
        <p v-if="structuredAnswer.text" class="iq-ai-answer-lead">{{ structuredAnswer.text }}</p>

        <template v-for="(block, blockIndex) in structuredAnswer.blocks">
          <p v-if="block.type === 'text'" :key="blockIndex" class="iq-ai-answer-paragraph">{{ block.text }}</p>

          <div v-else-if="block.type === 'entity_list'" :key="blockIndex" class="iq-ai-entity-list">
            <IQAnswerEntityRow
              v-for="row in cappedEntityRows(block)"
              :key="row.id || row.label"
              :label="row.label"
              :href="row.href"
              :metric-type="metricTypeOf(row)"
              :metric-value="metricValueOf(row)"
            />
            <IQAnswerViewAll
              v-if="showViewAll(block)"
              :total="block.total"
              :href="block.view_all_href"
            />
          </div>

          <IQAnswerSourceLink
            v-else-if="block.type === 'source_link'"
            :key="blockIndex"
            :title="block.title"
            :href="block.url"
          />

          <div
            v-else-if="block.type === 'disambiguation' && isDisambiguationEnabled"
            :key="blockIndex"
            class="iq-ai-disambiguation"
            data-testid="iq-ai-disambiguation"
          >
            <p :id="disambiguationPromptId(blockIndex)" class="iq-ai-disambiguation__prompt">{{ block.prompt }}</p>
            <div
              class="iq-ai-disambiguation__options"
              role="group"
              :aria-labelledby="disambiguationPromptId(blockIndex)"
            >
              <IQDisambiguationOption
                v-for="option in disambiguationOptions(block)"
                :key="option.employee_id"
                :label="option.label"
                :position-title="option.position_title || ''"
                :avatar="option.avatar || ''"
                :selected="isOptionChosen(option)"
                :disabled="isDisambiguationLocked"
                @select="handleDisambiguationSelect(option)"
              />
            </div>
          </div>
        </template>

        <IQAnswerSourceLink
          v-if="topSource"
          :title="topSource.title"
          :href="topSource.url"
        />

        <IQAnswerSnapshotMeta
          v-if="structuredAnswer.as_of"
          :as-of="structuredAnswer.as_of"
          @refresh="handleRefresh"
        />
      </div>

      <!-- Legacy string / {answer, sources, actions} answer -->
      <template v-else>
        <!-- Main Response Text -->
        <!-- "text" format: plain interpolation so markdown/HTML syntax shows literally -->
        <div
          v-if="(!isThinking || responseData.answer) && isPlainTextFormat"
          class="iq-ai-response-text"
        >{{ plainAnswer }}</div>
        <!-- markdown format: sanitized render (html:false + DOMPurify) -->
        <div
          v-else-if="!isThinking || responseData.answer"
          class="iq-ai-response-text"
          v-html="formattedAnswer"
        ></div>
        
        <!-- Action Buttons Section -->
        <div v-if="responseData.actions && responseData.actions.length > 0" class="actions-section">
          <button 
            v-for="(action, index) in responseData.actions" 
            :key="index" 
            class="action-button"
            @click="handleActionClick(action)"
          >
            {{ action.label }}
          </button>
        </div>
        
        <!-- Sources Section -->
        <div v-if="responseData.sources && responseData.sources.length > 0" class="sources-section">
          <h4 class="sources-heading">Sources</h4>
          <ol class="sources-list">
            <li v-for="(source, index) in responseData.sources" :key="index" class="source-item">
              <a
                v-if="resolveIQSourceHref(source)"
                :href="resolveIQSourceHref(source)"
                target="_blank"
                rel="noopener noreferrer"
                class="source-link"
              >
                <div class="source-icon">
                  <Icon name="document_icon" />
                </div>
                <span class="source-title">{{ source.title || resolveIQSourceHref(source) }}</span>
                <div class="external-link-icon">
                  <Icon name="external_link" />
                </div>
              </a>
              <span v-else class="source-title source-title--plain">{{ source.title }}</span>
            </li>
          </ol>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import Icon from "@components/icons/Icon.vue";
import IQThinkingDropdown from "@components/IQThinkingDropdown/IQThinkingDropdown.vue";
import IQAnswerEntityRow from "@components/IQAnswerEntityRow/IQAnswerEntityRow.vue";
import IQAnswerSourceLink from "@components/IQAnswerSourceLink/IQAnswerSourceLink.vue";
import IQAnswerViewAll from "@components/IQAnswerViewAll/IQAnswerViewAll.vue";
import IQAnswerSnapshotMeta from "@components/IQAnswerSnapshotMeta/IQAnswerSnapshotMeta.vue";
import IQDisambiguationOption from "@components/IQDisambiguationOption/IQDisambiguationOption.vue";
import { normalizeMentions } from "@components/AskAQuestionPrompt/mentionTokens";
import { LD_FLAG_IDEA_MDP_545, LD_FLAG_IDEA_MDP_582 } from "@components/constants/launchDarklyFlagKeys";
import { resolveIQSourceHref } from "@components/utils/iqSourceLink";

// Single shared parser. `html: false` means raw HTML in a model response is
// never parsed as markup (defence-in-depth with the DOMPurify pass below),
// `breaks: true` keeps a single \n a visible line break, and `linkify: true`
// turns bare URLs into links.
const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
});

const props = defineProps({
  response: {
    type: [String, Object],
    default: "",
  },
  /**
   * Whether the AI is currently in "thinking" phase (before generating response)
   */
  isThinking: {
    type: Boolean,
    default: false
  },
  /**
   * Current reasoning text being streamed during thinking phase
   */
  currentReasoning: {
    type: String,
    default: ''
  },
  /** `light`: white / pale theme for inline panels; default matches full IQ dark chat. */
  surface: {
    type: String,
    default: "default",
    validator: (v) => ["default", "light"].includes(v),
  },
  /**
   * IQ message format discriminator. "markdown" (default, includes messages with
   * no format field) goes through markdown-it; "text" renders via plain
   * interpolation so markdown syntax is shown literally.
   */
  format: {
    type: String,
    default: "markdown",
    validator: (v) => ["markdown", "text"].includes(v),
  },
  /**
   * Overrides the MDP-545 Ask AI LaunchDarkly flag for the structured
   * {text, blocks[], sources[]} rendering. Used by Storybook/tests; at runtime
   * the flag is read through proxy.$getFlag when the prop is false.
   */
  structuredAnswersEnabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Overrides the MDP-582 LaunchDarkly flag for the "Which <name>?" disambiguation
   * block. Used by Storybook/tests; at runtime the flag is read through
   * proxy.$getFlag when the prop is false.
   */
  disambiguationEnabled: {
    type: Boolean,
    default: false,
  },
  /**
   * employee_id of the option already chosen for the disambiguation block. Set by
   * the host when re-rendering persisted history; once set the block is locked.
   */
  selectedEmployeeId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['actionClick', 'refresh', 'disambiguation-select']);

// Computed property to handle both string and object response formats
const responseData = computed(() => {
  if (typeof props.response === 'string') {
    return { answer: props.response, sources: [], actions: [], reasoning: '' };
  }
  return props.response || { answer: '', sources: [], actions: [], reasoning: '' };
});

// Raw answer string used for the "text" (plain interpolation) branch.
const plainAnswer = computed(() => {
  const rawAnswer = responseData.value.answer;
  if (rawAnswer == null || rawAnswer === '') return '';
  return typeof rawAnswer === 'string' ? rawAnswer : String(rawAnswer);
});

const isPlainTextFormat = computed(() => props.format === 'text');

// Markdown render + sanitize. Empty for "text" messages — those are shown via
// plain interpolation and never pass through the parser.
const formattedAnswer = computed(() => {
  const text = plainAnswer.value;
  if (!text || isPlainTextFormat.value) return '';
  return DOMPurify.sanitize(md.render(text));
});

const handleActionClick = (action) => {
  emit('actionClick', action);
};

// ---- Structured Ask AI answer ({text, blocks[], sources[]}) ----
// Ask AI returns a structured contract (ER-12214 / ER-12229). Legacy IQ answers
// are strings or {answer, sources, actions, reasoning} and have no `blocks`.
const MAX_ENTITY_ROWS = 10;

const isStructuredAnswer = computed(() => {
  const r = props.response;
  return !!r && typeof r === 'object' && !Array.isArray(r) && Array.isArray(r.blocks);
});

// Structured blocks render only when the MDP-545 Ask AI flag is on.
const structuredAnswersFlag = ref(false);
const disambiguationFlag = ref(false);
const app = getCurrentInstance();
const { proxy } = app || {};

onMounted(async () => {
  try {
    if (proxy && typeof proxy.$getFlag === 'function') {
      structuredAnswersFlag.value = await proxy.$getFlag(LD_FLAG_IDEA_MDP_545, false);
    }
  } catch {
    structuredAnswersFlag.value = false;
  }
  try {
    if (proxy && typeof proxy.$getFlag === 'function') {
      disambiguationFlag.value = await proxy.$getFlag(LD_FLAG_IDEA_MDP_582, false);
    }
  } catch {
    disambiguationFlag.value = false;
  }
});

const isStructuredEnabled = computed(
  () => props.structuredAnswersEnabled || structuredAnswersFlag.value,
);

const isDisambiguationEnabled = computed(
  () => props.disambiguationEnabled || disambiguationFlag.value,
);

const structuredAnswer = computed(() => (isStructuredAnswer.value ? props.response : null));

const cappedEntityRows = (block) => (Array.isArray(block?.rows) ? block.rows.slice(0, MAX_ENTITY_ROWS) : []);

const showViewAll = (block) => {
  const total = Number(block?.total);
  return Number.isFinite(total) && total > cappedEntityRows(block).length;
};

const metricTypeOf = (row) => (row?.metric?.type === 'count' ? 'count' : 'progress');

const metricValueOf = (row) => {
  const value = Number(row?.metric?.value);
  return Number.isFinite(value) ? value : 0;
};

// The top EH User Guide source (Rule 14). Only rendered when the structured
// answer has a `sources` array; `source_link` blocks are handled separately.
const topSource = computed(() => {
  const sources = structuredAnswer.value?.sources;
  if (!Array.isArray(sources) || sources.length === 0) return null;
  const source = sources[0];
  return source && (source.title || source.url) ? source : null;
});

const handleRefresh = () => {
  emit('refresh');
};

// ---- "Which <name>?" disambiguation block (ER-12683) ----
const pickedEmployeeId = ref(null);

const chosenEmployeeId = computed(() => {
  const id = props.selectedEmployeeId ?? pickedEmployeeId.value;
  return id == null || id === '' ? null : String(id);
});

const isDisambiguationLocked = computed(() => chosenEmployeeId.value !== null);

const disambiguationPromptId = (blockIndex) => `iq-ai-disambiguation-prompt-${blockIndex}`;

const disambiguationOptions = (block) =>
  (Array.isArray(block?.options) ? block.options : []).filter(
    (option) => normalizeMentions([option]).length === 1,
  );

const isOptionChosen = (option) => String(option.employee_id) === chosenEmployeeId.value;

// Hands the host the mention payload (ER-12656) so it re-asks the same question.
const handleDisambiguationSelect = (option) => {
  if (isDisambiguationLocked.value) return;
  pickedEmployeeId.value = option.employee_id;
  emit('disambiguation-select', { mentions: normalizeMentions([option]) });
};

// Expose responseData and the render computeds for testing
defineExpose({
  responseData,
  plainAnswer,
  isPlainTextFormat,
  formattedAnswer,
  isStructuredAnswer,
  isStructuredEnabled,
  isDisambiguationEnabled,
  structuredAnswer,
});
</script>

<style scoped>
.iq-ai-response {
  display: flex;
  gap: 16px;
  width: 100%;
  background: #212121;
  border-radius: 24px;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
  max-width: 100%;
}
 
.iq-ai-response-icon-wrapper {
  flex-shrink: 0;
}

.iq-ai-response-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.iq-ai-response-text {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0%;
  color: #d9d9d9;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  margin: 0;
  max-width: 100%;
  overflow-x: hidden;
}

.iq-ai-response-text :deep(p) {
  margin: 0 0 12px 0;
}

.iq-ai-response-text :deep(p:last-child) {
  margin-bottom: 0;
}

.iq-ai-response-text :deep(li p) {
  margin: 0;
}

.iq-ai-response-text :deep(strong) {
  font-weight: 600;
  color: #ffffff;
}

.iq-ai-response-text :deep(em) {
  font-style: italic;
}

.iq-ai-response-text :deep(h1),
.iq-ai-response-text :deep(h2),
.iq-ai-response-text :deep(h3),
.iq-ai-response-text :deep(h4),
.iq-ai-response-text :deep(h5),
.iq-ai-response-text :deep(h6) {
  margin: 16px 0 8px 0;
  font-weight: 600;
  color: #ffffff;
  line-height: 1.3;
}

.iq-ai-response-text :deep(h1) {
  font-size: 18px;
}

.iq-ai-response-text :deep(h2) {
  font-size: 16px;
}

.iq-ai-response-text :deep(h3) {
  font-size: 15px;
}

.iq-ai-response-text :deep(h4),
.iq-ai-response-text :deep(h5),
.iq-ai-response-text :deep(h6) {
  font-size: 14px;
}

.iq-ai-response-text :deep(ul) {
  margin: 8px 0;
  padding-left: 20px;
  list-style: none;
}

.iq-ai-response-text :deep(ul li) {
  position: relative;
  padding-left: 12px;
  margin-bottom: 6px;
}

.iq-ai-response-text :deep(ul li::before) {
  content: '•';
  position: absolute;
  left: 0;
  color: #3b82f6;
}

.iq-ai-response-text :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
}

.iq-ai-response-text :deep(ol li) {
  margin-bottom: 6px;
}

.iq-ai-response-text :deep(a) {
  color: #3b82f6;
  text-decoration: underline;
}

.iq-ai-response-text :deep(code) {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 13px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  padding: 2px 4px;
}

/* Sources Section Styles */
.sources-section {
  margin-top: 8px;
}

.sources-heading {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #60a5fa;
  margin: 0 0 16px 0;
}

.sources-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  counter-reset: source-counter;
}

.source-item {
  counter-increment: source-counter;
  display: flex;
  align-items: center;
  gap: 12px;
}

.source-item::before {
  content: counter(source-counter) ".";
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  color: #d9d9d9;
  min-width: 20px;
}

.source-link {
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  transition: opacity 0.2s ease;
}

.source-link:hover {
  opacity: 0.8;
}

.source-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.source-title {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #60a5fa;
  text-decoration: underline;
}

.external-link-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  margin-left: auto;
}

/* Icon styling for external link */
.external-link-icon :deep(svg) {
  width: 12px;
  height: 12px;
  fill: #3b82f6;
}

/* Icon styling for document icon */
.source-icon :deep(svg) {
  width: 16px;
  height: 16px;
  fill: #3b82f6;
}

/* Action Buttons Section */
.actions-section {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.action-button {
  background: #FFFFFF;
  color: #000000;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
}

.action-button:hover {
  background: #F5F5F5;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
}

.action-button:active {
  transform: scale(0.98);
}

/* Inline / mini panels: pale card on white page */
.iq-ai-response--light {
  background: #ffffff;
  border: 1px solid #eaecf0;
}

.iq-ai-response--light .iq-ai-response-text {
  color: #344054;
}

.iq-ai-response--light .iq-ai-response-text :deep(strong),
.iq-ai-response--light .iq-ai-response-text :deep(h1),
.iq-ai-response--light .iq-ai-response-text :deep(h2),
.iq-ai-response--light .iq-ai-response-text :deep(h3),
.iq-ai-response--light .iq-ai-response-text :deep(h4),
.iq-ai-response--light .iq-ai-response-text :deep(h5),
.iq-ai-response--light .iq-ai-response-text :deep(h6) {
  color: #101828;
}

.iq-ai-response--light .iq-ai-response-text :deep(code) {
  background: #f2f4f7;
  color: #344054;
}

.iq-ai-response--light .source-item::before {
  color: #667085;
}

.iq-ai-response--light .sources-heading {
  color: #2563eb;
}

.iq-ai-response--light .source-title {
  color: #2563eb;
}

/* ---- Structured Ask AI answer ({text, blocks[], sources[]}) ---- */
.iq-ai-answer-blocks {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #f2f4f7;
  border-radius: 12px;
  box-sizing: border-box;
  max-width: 100%;
}

.iq-ai-answer-lead,
.iq-ai-answer-paragraph {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
  margin: 0;
  word-break: break-word;
  overflow-wrap: break-word;
}

.iq-ai-entity-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.iq-ai-disambiguation {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.iq-ai-disambiguation__prompt {
  font-family: Inter;
  font-weight: 400;
  font-size: 15px;
  line-height: 22px;
  color: #d9d9d9;
  margin: 0;
  word-break: break-word;
  overflow-wrap: break-word;
}

.iq-ai-response--light .iq-ai-disambiguation__prompt {
  color: #111727;
}

.iq-ai-disambiguation__options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
